import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Phone, TrendingUp, ArrowLeft, Loader2 } from 'lucide-react';
import { advisorInfo } from '../data/mock';
import Header from './Header';
import Footer from './Footer';

const SharedCalculation = () => {
  const { calcId } = useParams();
  const navigate = useNavigate();
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCalculation = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/calculator/shared/${calcId}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Calculation not found');
          } else if (response.status === 410) {
            throw new Error('This calculation link has expired');
          }
          throw new Error('Failed to load calculation');
        }

        const data = await response.json();
        setCalculation(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCalculation();
  }, [calcId]);

  const formatPrice = (price) => {
    return `RM${price.toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-pink-600 mx-auto mb-4" />
          <p className="text-lg text-slate-700">Loading calculation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-6xl mb-4">😢</div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">{error}</h2>
            <p className="text-slate-600 mb-6">
              {error.includes('expired') 
                ? 'This calculation link has expired after 30 days.'
                : 'The calculation you\'re looking for doesn\'t exist.'}
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-pink-600 hover:bg-pink-700 text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Calculator
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-100">
      <Header />
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Badge className="bg-pink-100 text-pink-800 mb-4">Saved Calculation</Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              Loan Calculation Result
            </h1>
            <p className="text-slate-600">
              Shared by Syasyah Perodua Advisor
            </p>
          </div>

          <Card className="bg-white shadow-2xl rounded-2xl border-0 mb-6">
            <CardHeader>
              <CardTitle className="text-xl font-bold text-slate-900 flex items-center space-x-2">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <span>Hasil Kiraan Loan</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Car Model Info */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6">
                <h3 className="font-bold text-blue-900 text-xl mb-2">{calculation.model_name}</h3>
                <div className="bg-blue-100 rounded-full px-4 py-2 inline-block">
                  <span className="text-sm font-semibold text-blue-800">
                    {calculation.variant_name}
                  </span>
                </div>
                
                <div className="space-y-3 mt-4">
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3">
                    <span className="text-sm text-blue-700 font-medium">Harga OTR:</span>
                    <span className="text-lg font-bold text-blue-900">
                      {formatPrice(calculation.otr_price)}
                    </span>
                  </div>
                  
                  {calculation.effective_rebate > 0 && (
                    <div className="flex justify-between items-center py-3 px-4 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg">
                      <span className="text-sm font-bold text-orange-700 flex items-center">
                        <span className="mr-2">🎁</span>
                        Rebate:
                      </span>
                      <span className="text-lg font-bold text-red-600">
                        - {formatPrice(calculation.effective_rebate)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center bg-green-50 rounded-lg px-4 py-3 border-2 border-green-300">
                    <span className="text-sm font-bold text-green-700">Harga Final:</span>
                    <span className="text-xl font-bold text-green-600">
                      {formatPrice(calculation.price_after_rebate)}
                    </span>
                  </div>
                  
                  {calculation.trade_in_value > 0 && (
                    <div className="flex justify-between items-center bg-blue-50 rounded-lg px-4 py-3">
                      <span className="text-sm font-medium text-blue-700">🚗 Trade-In:</span>
                      <span className="text-lg font-bold text-blue-900">
                        {formatPrice(calculation.trade_in_value)}
                      </span>
                    </div>
                  )}
                  
                  {calculation.custom_deposit > 0 && (
                    <div className="flex justify-between items-center bg-purple-50 rounded-lg px-4 py-3">
                      <span className="text-sm font-medium text-purple-700">💵 Deposit:</span>
                      <span className="text-lg font-bold text-purple-900">
                        {formatPrice(calculation.custom_deposit)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center bg-white rounded-lg px-4 py-3">
                    <span className="text-sm text-blue-700 font-medium">Interest Rate:</span>
                    <span className="text-sm font-bold text-blue-900">
                      {calculation.interest_rate}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Monthly Payment Highlight */}
              <Card className="bg-gradient-to-br from-orange-500 to-red-600 text-white border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-orange-100 mb-2">
                      Ansuran Bulanan ({calculation.loan_years} Tahun)
                    </p>
                    <div className="text-4xl md:text-5xl font-bold mb-2">
                      {formatPrice(calculation.monthly_payment)}
                      <span className="text-lg">/bln</span>
                    </div>
                    <Badge className="bg-yellow-400 text-orange-900 font-bold">
                      BEST RATE
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <div className="grid md:grid-cols-2 gap-4">
                <Button 
                  onClick={() => {
                    const message = `[Website] PM PERODUA - Hi Syasyah, saya tengok shared calculation ni dan berminat. ${calculation.model_name} ${calculation.variant_name}, ansuran ${formatPrice(calculation.monthly_payment)}/bulan. Boleh proceed?`;
                    window.open(`${advisorInfo.whatsappLink}?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 text-lg"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  WhatsApp Syasyah
                </Button>
                
                <Button 
                  onClick={() => navigate('/calculator')}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-4 text-lg"
                >
                  Try Calculator
                </Button>
              </div>

              {/* Info */}
              <div className="text-center text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                💡 This is an estimated calculation. Final approval depends on bank assessment.
              </div>
            </CardContent>
          </Card>

          <div className="text-center">
            <Button 
              onClick={() => navigate('/')}
              variant="outline"
              className="text-slate-600"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Homepage
            </Button>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default SharedCalculation;
